import { Button } from "@component/button";
import { trackEvent } from "@util/mixpanel";
import { useQuery } from "@util/useQuery";
import { CheckCircleIcon } from "lucide-react";
import { useState } from "react";
import { getPostAuthLandingPath, sanitizeRedirectPath } from "@util/auth-redirect";

interface Props {
    isSkipInterview: boolean
    redirect_path: string
}

export function CandidateAIInterviewCompleted(props: Props) {
    const queryParams = useQuery();
    const [isLoading, setLoading] = useState<boolean>(false);

    const handleContinue = () => {
        setLoading(true);

        // This screen is only rendered for job_seekers (post-interview in
        // the signup flow), so the landing path is the job_seeker-specific
        // one. The optional `redirect_path` prop is sanitised so a tampered
        // value can't bounce the user off-domain.
        const fallback = getPostAuthLandingPath("job_seeker");

        if (!props.redirect_path) {
            window.location.href = fallback;
        } else {
            trackEvent("continue_to_apply_clicked", {
                job_id: queryParams.get("ref") || "",
            })
            setTimeout(() => {
                setLoading(false);
                window.location.href = sanitizeRedirectPath(props.redirect_path, fallback);
            }, 2000);
        }
    }

    return (
        <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
                <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {!props.isSkipInterview ? "Interview Completed!" : "Ready When You Are"}
            </h1>

            {!props.isSkipInterview ? (
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                    Your responses are saved and your profile is updated. <br />
                    Head to your Dashboard to see your job matches and submit applications now.
                </p>
            ) : (
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                    {import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true" ? (
                        <>
                            You can explore interview scenarios without starting a simulation for now.<br />
                            Complete a simulation anytime to assess your readiness and improve your interview performance.

                            <br />
                            <br />
                            Head to your Dashboard to choose an interview scenario and start practising when you’re ready.
                        </>
                    ) : (
                        <>
                            You can explore roles without your AI Interview for now.<br />
                            Complete it anytime to help companies learn about you and improve your chances of getting hired.

                            <br />
                            <br />
                            Head to your Dashboard to browse jobs and take the interview when you’re ready.
                        </>
                    )}
                </p>
            )}

            <Button
                variant="default"
                onClick={handleContinue}
                disabled={isLoading}
                className="px-6 rounded-full"
            >
                {isLoading ? "Redirecting..." : (
                    props.redirect_path === "" ? !props.isSkipInterview ? "View My Dashboard" : "Go to Dashboard" : "Continue to Apply"
                )}
            </Button>
        </div>
    )
}